import { create } from 'zustand';
import { api } from '../services/api';
import type {
  ModelRegistryEntry,
  TaskTierConfig,
  TierInfo,
  ModelPricingMap,
  OpenRouterCatalogEntry,
  RefreshReport,
} from '../types';
import type { RoutingPresets, CostReport } from '../types/config';

// Canonical task labels — must match SEEDED_TASK_MODEL_CONFIG keys in
// workers/src/services/modelRouting.js.
const TASK_LABELS: Record<string, string> = {
  stage1_definition: 'Stage 1: Definition & Metadata',
  stage1b_fakes: 'Stage 1b: Fake Definitions',
  reveal_lines: 'Reveal Lines (per fake)',
  assignment: 'Assignment',
  slide1_hook: 'Slide 1 Hook',
  fake_judge: 'Fake Judge (rates Stage 1b fakes; use a different model from Stage 1b)',
  fake_shape: 'Fake Shape (names the kind of thing the real definition is, for Stage 1b)',
  joke_pattern_pick: 'Joke Pattern (recommends the word\'s joke pattern and says why, for Stage 1)',
  stage2_examples: 'Stage 2: Example Sentences',
  stage3_howtouse: 'Stage 3: How To Use',
  stage4_funfacts: 'Stage 4: Fun Facts',
  // stage7_social + stage9_relatedsocial retired 2026-07-05 (Social Engine) —
  // removed so Settings stops rendering the retired task rows. stage6_reframes,
  // stage8_related, social_generation and social_game_tease retired the same
  // way 2026-10-22 (their routes return 410); their KV values are kept.
  chat: 'Chat',
  learning_extraction: 'Learning Layer Extraction',
  regenerate_compare: 'Regenerate & Compare',
};

export { TASK_LABELS };

// Retired tasks whose task_model_config values stay in KV but are never shown.
const RETIRED_TASKS = new Set(['stage6_reframes', 'stage8_related', 'social_generation', 'social_game_tease']);

interface ConfigState {
  config: Record<string, unknown> | null;
  loading: boolean;
  error: string | null;
  ollamaModels: string[];
  modelRegistry: ModelRegistryEntry[];
  taskConfig: TaskTierConfig;
  tiers: TierInfo[];
  inheritsSentinel: string;
  modelRoutingLoaded: boolean;
  modelRoutingError: string | null;
  autoGenerateNext: boolean;

  // Model refresh + auto-pricing
  modelPricing: ModelPricingMap;
  openrouterCatalog: OpenRouterCatalogEntry[];
  refreshing: boolean;
  refreshReport: RefreshReport | null;
  refreshError: string | null;

  // Routing presets
  presets: RoutingPresets;
  presetsError: string | null;

  // QA banned phrases
  bannedPhrases: string[];
  qaConfigError: string | null;

  // Cost meter
  costReport: CostReport | null;

  fetchConfig: () => Promise<void>;
  updateConfig: (updates: Record<string, unknown>) => Promise<void>;
  fetchAutoGenerate: () => Promise<void>;
  toggleAutoGenerate: (enabled: boolean) => Promise<void>;
  fetchOllamaModels: () => Promise<void>;
  fetchModelRouting: () => Promise<void>;
  updateTaskAssignment: (taskName: string, tier: string) => Promise<void>;
  saveModelRegistry: (registry: ModelRegistryEntry[]) => Promise<void>;
  addModel: (entry: ModelRegistryEntry) => Promise<void>;
  updateModel: (displayName: string, patch: Partial<ModelRegistryEntry>) => Promise<void>;
  deleteModel: (displayName: string) => Promise<void>;
  tasksUsingModel: (displayName: string) => string[];

  // Model refresh + auto-pricing
  fetchModelPricing: () => Promise<void>;
  fetchOpenRouterCatalog: () => Promise<void>;
  refreshModels: () => Promise<void>;
  addOpenRouterModel: (id: string, displayName?: string) => Promise<void>;

  // Routing presets
  fetchPresets: () => Promise<void>;
  savePreset: (name: string) => Promise<void>;
  applyPreset: (name: string) => Promise<void>;
  deletePreset: (name: string) => Promise<void>;

  // QA banned phrases
  fetchQaConfig: () => Promise<void>;
  saveBannedPhrases: (bannedPhrases: string[]) => Promise<void>;

  // Cost meter
  fetchCost: () => Promise<void>;
}

export const useConfigStore = create<ConfigState>()((set, get) => ({
  // App config (providers, airtable status)
  config: null,
  loading: false,
  error: null,
  ollamaModels: [],
  autoGenerateNext: true,

  // Model routing state
  modelRegistry: [],
  taskConfig: {},
  tiers: [],
  inheritsSentinel: 'inherits_from_stage',
  modelRoutingLoaded: false,
  modelRoutingError: null,

  // Model refresh + auto-pricing
  modelPricing: {},
  openrouterCatalog: [],
  refreshing: false,
  refreshReport: null,
  refreshError: null,

  // Routing presets
  presets: {},
  presetsError: null,

  // QA banned phrases
  bannedPhrases: [],
  qaConfigError: null,

  // Cost meter
  costReport: null,

  fetchConfig: async () => {
    set({ loading: true, error: null });
    try {
      const config = await api.get('/api/config') as Record<string, unknown>;
      set({ config, loading: false });
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  fetchAutoGenerate: async () => {
    try {
      const data = await api.get('/api/config/auto-generate') as { enabled: boolean };
      set({ autoGenerateNext: data.enabled });
    } catch {
      // default stays true
    }
  },

  toggleAutoGenerate: async (enabled: boolean) => {
    set({ autoGenerateNext: enabled });
    try {
      await api.put('/api/config/auto-generate', { enabled });
    } catch {
      set({ autoGenerateNext: !enabled });
    }
  },

  updateConfig: async (updates: Record<string, unknown>) => {
    try {
      const result = await api.put('/api/config', updates) as { config: Record<string, unknown> };
      set({ config: { ...get().config, ...result.config } });
    } catch (err) {
      set({ error: (err as Error).message });
      throw err;
    }
  },

  fetchOllamaModels: async () => {
    try {
      const data = await api.get('/api/config/ollama-models') as { models?: string[] };
      set({ ollamaModels: data.models || [] });
    } catch {
      set({ ollamaModels: [] });
    }
  },

  fetchModelRouting: async () => {
    try {
      const [tasksRes, modelsRes, tiersRes] = await Promise.all([
        api.get('/api/config/tasks') as Promise<{ tasks?: TaskTierConfig }>,
        api.get('/api/config/models') as Promise<{ models?: ModelRegistryEntry[] }>,
        api.get('/api/config/tiers') as Promise<{ tiers?: TierInfo[]; inheritsSentinel?: string }>,
      ]);
      set({
        taskConfig: tasksRes.tasks || {},
        modelRegistry: modelsRes.models || [],
        tiers: tiersRes.tiers || [],
        inheritsSentinel: tiersRes.inheritsSentinel || 'inherits_from_stage',
        modelRoutingLoaded: true,
        modelRoutingError: null,
      });
    } catch (err) {
      set({ modelRoutingError: (err as Error).message, modelRoutingLoaded: true });
    }
  },

  updateTaskAssignment: async (taskName: string, tier: string) => {
    const current = get().taskConfig;
    const next = { ...current, [taskName]: tier };
    set({ taskConfig: next });
    try {
      const result = await api.put('/api/config/tasks', { tasks: next }) as { tasks: TaskTierConfig };
      set({ taskConfig: result.tasks });
    } catch (err) {
      set({ taskConfig: current, modelRoutingError: (err as Error).message });
      throw err;
    }
  },

  saveModelRegistry: async (registry: ModelRegistryEntry[]) => {
    const previous = get().modelRegistry;
    set({ modelRegistry: registry });
    try {
      const result = await api.put('/api/config/models', { models: registry }) as { models: ModelRegistryEntry[] };
      set({ modelRegistry: result.models });
    } catch (err) {
      set({ modelRegistry: previous, modelRoutingError: (err as Error).message });
      throw err;
    }
  },

  addModel: async (entry: ModelRegistryEntry) => {
    const next = [...get().modelRegistry, entry];
    await get().saveModelRegistry(next);
  },

  updateModel: async (displayName: string, patch: Partial<ModelRegistryEntry>) => {
    const next = get().modelRegistry.map((m) =>
      m.displayName === displayName ? { ...m, ...patch } : m
    );
    await get().saveModelRegistry(next);
  },

  deleteModel: async (displayName: string) => {
    const next = get().modelRegistry.filter((m) => m.displayName !== displayName);
    await get().saveModelRegistry(next);
  },

  // Returns an array of task names that currently reference `displayName`.
  // Under tier-based routing, task values are tier names; a registry entry
  // is "in use" when one of the tiers resolves to that displayName.
  tasksUsingModel: (displayName: string) => {
    const { taskConfig, tiers } = get();
    const tiersUsingDisplayName = new Set<string>(
      tiers.filter((t) => t.displayName === displayName).map((t) => t.tier)
    );
    return Object.entries(taskConfig)
      .filter(([name]) => !RETIRED_TASKS.has(name))
      .filter(([, value]) => typeof value === 'string' && tiersUsingDisplayName.has(value))
      .map(([name]) => name);
  },

  // ---- Model refresh + auto-pricing ----
  fetchModelPricing: async () => {
    try {
      const data = await api.get('/api/config/model-pricing') as { pricing?: ModelPricingMap };
      set({ modelPricing: data.pricing || {} });
    } catch {
      // leave prior pricing in place
    }
  },

  fetchOpenRouterCatalog: async () => {
    try {
      const data = await api.get('/api/config/openrouter-catalog') as { models?: OpenRouterCatalogEntry[] };
      set({ openrouterCatalog: data.models || [] });
    } catch {
      set({ openrouterCatalog: [] });
    }
  },

  refreshModels: async () => {
    set({ refreshing: true, refreshError: null });
    try {
      const result = await api.post('/api/config/refresh-models', {}) as {
        models?: ModelRegistryEntry[];
        pricing?: ModelPricingMap;
        catalog?: OpenRouterCatalogEntry[];
        report?: RefreshReport;
      };
      set({
        modelRegistry: result.models || get().modelRegistry,
        modelPricing: result.pricing || {},
        openrouterCatalog: result.catalog || [],
        refreshReport: result.report || null,
        refreshing: false,
      });
    } catch (err) {
      set({ refreshError: (err as Error).message, refreshing: false });
    }
  },

  addOpenRouterModel: async (id: string, displayName?: string) => {
    try {
      const result = await api.post('/api/config/models/openrouter', { id, displayName }) as {
        models?: ModelRegistryEntry[];
        pricing?: ModelPricingMap;
      };
      set({
        modelRegistry: result.models || get().modelRegistry,
        modelPricing: result.pricing || get().modelPricing,
      });
    } catch (err) {
      set({ modelRoutingError: (err as Error).message });
      throw err;
    }
  },

  // ---- Routing presets ----
  fetchPresets: async () => {
    try {
      const data = await api.get('/api/config/presets') as { presets?: RoutingPresets };
      set({ presets: data.presets || {}, presetsError: null });
    } catch (err) {
      set({ presetsError: (err as Error).message });
    }
  },

  savePreset: async (name: string) => {
    const tasks = get().taskConfig;
    try {
      await api.post('/api/config/presets', { name, tasks });
      await get().fetchPresets();
    } catch (err) {
      set({ presetsError: (err as Error).message });
      throw err;
    }
  },

  applyPreset: async (name: string) => {
    try {
      await api.put('/api/config/presets', { name });
      // Preset becomes the active task config — refetch tasks.
      await get().fetchModelRouting();
    } catch (err) {
      set({ presetsError: (err as Error).message });
      throw err;
    }
  },

  deletePreset: async (name: string) => {
    try {
      await api.delete(`/api/config/presets?name=${encodeURIComponent(name)}`);
      await get().fetchPresets();
    } catch (err) {
      set({ presetsError: (err as Error).message });
      throw err;
    }
  },

  // ---- QA banned phrases ----
  fetchQaConfig: async () => {
    try {
      const data = await api.get('/api/qa/config') as { bannedPhrases?: string[] };
      set({ bannedPhrases: data.bannedPhrases || [], qaConfigError: null });
    } catch (err) {
      set({ qaConfigError: (err as Error).message });
    }
  },

  saveBannedPhrases: async (bannedPhrases: string[]) => {
    const previous = get().bannedPhrases;
    set({ bannedPhrases });
    try {
      const data = await api.put('/api/qa/config', { bannedPhrases }) as { bannedPhrases?: string[] };
      set({ bannedPhrases: data.bannedPhrases || bannedPhrases, qaConfigError: null });
    } catch (err) {
      set({ bannedPhrases: previous, qaConfigError: (err as Error).message });
      throw err;
    }
  },

  // ---- Cost meter ----
  fetchCost: async () => {
    try {
      const data = await api.get('/api/config/cost') as CostReport;
      set({ costReport: data });
    } catch {
      // leave prior report in place
    }
  },
}));
